import {
  App,
  Button,
  Space,
  Table,
} from 'antd'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import GroupTree from '@/components/fields/group-tree'
import FunctionPermission from '@/components/permissions/function-permission'
import Page from '@/components/shared/page'
import { FunctionKey } from '@/constants/function-key'
import useFormModal from '@/hooks/use-form-modal'
import { Creator } from '@/model/creator'
import { GroupType } from '@/model/group'
import { Role } from '@/model/role'
import { readAllCreators } from '@/redux/creator-actions'
import { selectAccessibleCreators } from '@/redux/creator-selector'
import {
  createEmployee,
  CreateEmployeeParams,
  readAllEmployees,
  updateEmployee,
  UpdateEmployeeParams,
} from '@/redux/employee-actions'
import { selectAccessibleEmployees } from '@/redux/employee-selector'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import BatchOperationsOptions from './components/batch-operations-options'
import EmployeeFields from './components/employee-fields'
import SearchEmployee, { SearchEmployeeValues } from './components/search-employee'
import useBatchOperations from './hooks/use-batch-operations'
import useEmployeeColumns, { Record } from './hooks/use-employee-columns'

const selector = createAppSelector([
  selectAccessibleCreators,
  selectAccessibleEmployees,
  (state) => state.group.groups,
  (state) => state.role.roles,
  (state) => state.creator.creatorsFetching,
  (state) => state.employee.employeesFetching,
], (
  creators,
  employees,
  groups,
  roles,
  creatorsFetching,
  employeesFetching,
): {
  dataSource: Record[]
  fetching: boolean
} => {
  return {
    dataSource: employees.map((employee) => {
      const group = groups.find(({ id }) => id === employee.groupId)
      return {
        ...employee,
        groupIds: group ? [...group.ancestorIds, group.id] : [],
        roles: employee
          .roleIds
          .map((roleId) => roles.find(({ id }) => id === roleId))
          .filter((item): item is Role => item !== undefined),
        assignedCreators: employee
          .creatorIds
          .map((creatorId) => creators.find(({ id }) => id === creatorId))
          .filter((item): item is Creator => item !== undefined),
      }
    }),
    fetching: creatorsFetching || employeesFetching,
  }
},
)

export default function ManageEmployees() {
  const { dataSource, fetching } = useAppSelector(selector)
  const [selectedGroupId, setSelectedGroupId] = useState<number>()
  const [searchValues, setSearchValues] = useState<SearchEmployeeValues>()

  const dispatch = useAppDispatch()
  const { message } = App.useApp()

  useEffect(() => {
    void dispatch(readAllCreators())
    void dispatch(readAllEmployees())
  }, [dispatch])

  const { rowSelection, selectedRowKeys } = useBatchOperations()

  const onCreateEmployee = useCallback(
    async (values: CreateEmployeeParams) => {
      await dispatch(createEmployee(values)).unwrap()
      await dispatch(readAllEmployees())
      void dispatch(readAllCreators())
      void message.success('Add employee success')
    },
    [dispatch, message],
  )
  const [createFormModal, openCreateFormModal] = useFormModal({
    onSubmit: onCreateEmployee,
    getFields: (form) => <EmployeeFields form={form} />,
    title: 'Add Employee',
  })
  const onUpdateEmployee = useCallback(
    async (values: UpdateEmployeeParams) => {
      await dispatch(updateEmployee(values)).unwrap()
      await dispatch(readAllEmployees())
      void dispatch(readAllCreators())
      void message.success('Update employee success')
    },
    [dispatch, message],
  )
  const [updateFormModal, openUpdateFormModal] = useFormModal({
    onSubmit: onUpdateEmployee,
    getFields: (form) => <EmployeeFields form={form} />,
    title: 'Edit Employee',
  })
  const columns = useEmployeeColumns(openUpdateFormModal)
  const filteredDataSource = useMemo(() => {
    let result = dataSource
    if (selectedGroupId) {
      result = result.filter(({ groupIds }) =>
        groupIds.includes(selectedGroupId),
      )
    }
    if (searchValues) {
      const { name, creatorId, status } = searchValues
      if (name) {
        result = result.filter((item) =>
          item.name.toLowerCase().includes(name.toLowerCase()),
        )
      }
      if (creatorId) {
        result = result.filter((item) => item.creatorIds.includes(creatorId))
      }
      if (status) {
        result = result.filter((item) => item.status === status)
      }
    }
    return result
  }, [dataSource, selectedGroupId, searchValues])

  return (
    <Page
      headerRight={
        <Space>
          <BatchOperationsOptions employeeIds={selectedRowKeys} />
          <FunctionPermission functionKey={FunctionKey.EmployeesManageAddEmployee}>
            <Button type="primary" onClick={openCreateFormModal}>
              + Add Employee
            </Button>
          </FunctionPermission>
        </Space>
      }
      sider={
        <GroupTree
          editable
          type={GroupType.Employee}
          value={selectedGroupId}
          onChange={setSelectedGroupId}
          ellipsis
        />
      }
      siderWidth={260}
    >
      <SearchEmployee onSearch={setSearchValues} />
      <Table
        loading={fetching}
        dataSource={filteredDataSource}
        columns={columns}
        rowKey="id"
        rowSelection={rowSelection}
      />
      {createFormModal}
      {updateFormModal}
    </Page>
  )
}
